import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Drawer, EmptyState, Inline, Link, Panel, Skeleton, Stack, t, Text } from '@kanbios/ui';
import type { fleetState } from '@kanbios/api-client';
import { api, unwrap } from '../lib/api';
import { shortDate } from '../lib/format';
import { ListTable } from './ListTable';
import { runningList } from './RunningPanel';

type Running = fleetState.components['schemas']['Running'];
export type Install = NonNullable<Running['wordpress']>[number];
type Php = NonNullable<Running['php']>[number];
type Vulnerability = fleetState.components['schemas']['Vulnerability'];
type Severity = NonNullable<Vulnerability['severity']>;

const SEVERITY_TONES = { critical: 'neg', high: 'neg', medium: 'warn', low: 'neutral' } as const;
const ORDER: (Severity | null)[] = ['critical', 'high', 'medium', 'low', null];

const worst = (list: Vulnerability[]) => [...list].sort((a, b) => ORDER.indexOf(a.severity) - ORDER.indexOf(b.severity))[0]?.severity ?? null;

/** D39: one component of an install with a known vulnerability (the server lists them, the worst first). */
interface Finding { id: string; install: string; kind: 'core' | 'plugin' | 'theme'; name: string; version: string | null; v: Vulnerability }

function InstallState({ install }: { install: Install }) {
  const all = [...install.core, ...install.plugins.flatMap(p => p.vulnerabilities), ...install.themes.flatMap(p => p.vulnerabilities)];
  if (all.length) {
    const severity = worst(all);
    return <Badge tone={severity === 'critical' || severity === 'high' ? 'neg' : 'warn'} size="sm">{t(severity === 'critical' ? 'appSecurity.state.critical' : 'appSecurity.state.vulnerable', { n: all.length })}</Badge>;
  }
  return install.checked
    ? <Badge tone="pos" size="sm">{t('appSecurity.state.none')}</Badge>
    : <Badge tone="neutral" size="sm">{t('appSecurity.state.unchecked')}</Badge>;
}

function PhpState({ php }: { php: Php }) {
  if (php.endOfLife) return <Badge tone="neg" size="sm">{t('appSecurity.php.endOfLife', { date: shortDate(php.endOfLife) })}</Badge>;
  if (php.securityRelease) return <Badge tone="neg" size="sm">{t('appSecurity.php.securityRelease', { version: php.securityRelease })}</Badge>;
  if (php.followedBy === 'distribution') return <Badge tone="neutral" size="sm">{t('appSecurity.php.byDistribution')}</Badge>;
  return php.checked ? <Badge tone="pos" size="sm">{t('appSecurity.php.upToDate')}</Badge> : <Badge tone="neutral" size="sm">{t('appSecurity.state.unchecked')}</Badge>;
}

/**
 * D39, « Logiciels » tab: the WordPress installs and the PHP
 * versions the agent reads on the server, with the vulnerabilities known
 * against them (WPVulnerability, php.net, endoflife.date). Information only:
 * applying the fixes comes later (lot 3).
 */
export function AppSecurityPanels({ server, only }: {
  server: string;
  /**
   * D47: within an « Applications » block, the installs of that deployment, and PHP or not. D120: as the list's
   * filters (`domain`: the deployment's sites; `claimed: no`: what no declared deployment serves).
   */
  only?: { wordpress: { domain: string } | { claimed: 'no' }; php: boolean };
}) {
  const [open, setOpen] = useState<Install | null>(null);
  // The same query as the other « Ce qui tourne » tabs: one reading for all.
  const running = useQuery({
    queryKey: ['server-running', server],
    queryFn: async () => unwrap(await api.fleetState.GET('/servers/{server}/running', { params: { path: { server } } })),
    refetchInterval: 60_000,
  });
  const r = running.data;
  if (running.isPending) return <Panel title={t('appSecurity.wordpress.title')}><Skeleton lines={3} /></Panel>;
  if (!r || r.restricted || !r.installed) return null;
  if (r.wordpress === null && r.php === null) {
    if (only) return null;
    return (
      <Panel title={t('appSecurity.wordpress.title')}>
        <EmptyState framed={false} icon="update" title={t('appSecurity.notRead.title')}>{t('appSecurity.notRead.body')}</EmptyState>
      </Panel>
    );
  }
  // A server with no WordPress and no PHP shows neither panel.
  // Whether each panel has anything to show (its rows are the lists, D120).
  const claimed = new Set(r.sites.filter(x => x.attachedTo).flatMap(x => x.domains));
  const wanted = only?.wordpress;
  const keeps = (i: Install) => !wanted || ('domain' in wanted ? i.domains.some(d => wanted.domain.split(',').includes(d)) : !i.domains.some(d => claimed.has(d)));
  const wordpress = (r.wordpress ?? []).filter(keeps);
  const php = !only || only.php ? r.php ?? [] : [];
  if (!wordpress.length && !php.length) return null;
  const fixedWordpress: Record<string, string> | undefined = wanted ? { ...wanted } : undefined;
  return (
    <Stack gap={16}>
      {wordpress.length > 0 && <Panel title={t('appSecurity.wordpress.title')} subtitle={t('appSecurity.wordpress.subtitle')} flush>
        <ListTable<Install>
          label={t('appSecurity.wordpress.title')}
          queryKey={['server-running-list', server, 'wordpress', fixedWordpress ?? null]}
          fetch={runningList<Install>(server, 'wordpress')}
          fixed={fixedWordpress}
          rowKey="path"
          defaultSort={{ key: 'state', dir: 'asc' }}
          search={t('appSecurity.wordpress.search')}
          facets={[{ key: 'state', label: t('appSecurity.wordpress.state'), valueLabel: v => t(`appSecurity.wordpress.states.${v}`) }]}
          refetchInterval={60_000}
          onRowClick={(i: Install) => setOpen(i)}
          activeRow={(i: Install) => i.path === open?.path}
          columns={[
            { key: 'path', label: t('appSecurity.wordpress.path'), mono: true, strong: true, grow: true, width: 240 },
            { key: 'state', label: t('appSecurity.wordpress.state'), width: 220, render: (i: Install) => <InstallState install={i} /> },
            { key: 'version', label: t('appSecurity.wordpress.version'), mono: true, width: 90 },
            { key: 'plugins', label: t('appSecurity.wordpress.plugins'), type: 'number', width: 90, sortable: false, accessor: (i: Install) => i.plugins.length },
            { key: 'themes', label: t('appSecurity.wordpress.themes'), type: 'number', width: 90, sortable: false, accessor: (i: Install) => i.themes.length },
            // The sites the install serves, on the same line as the rest.
            { key: 'domains', label: t('running.sites.domains'), mono: true, width: 220, sortable: false, accessor: (i: Install) => i.domains.join(', ') || '—' },
          ]}
          empty={<EmptyState framed={false} icon="web" title={t('appSecurity.wordpress.empty')} />}
        />
      </Panel>}
      {php.length > 0 && <Panel title={t('appSecurity.php.title')} subtitle={t('appSecurity.php.subtitle')} flush>
        <ListTable<Php>
          label={t('appSecurity.php.title')}
          queryKey={['server-running-list', server, 'php']}
          fetch={runningList<Php>(server, 'php')}
          rowKey="branch"
          defaultSort={{ key: 'branch', dir: 'asc' }}
          search={t('appSecurity.php.search')}
          facets={[
            { key: 'state', label: t('appSecurity.php.state'), valueLabel: v => t(`appSecurity.php.states.${v}`) },
            { key: 'origin', label: t('appSecurity.php.origin'), valueLabel: v => t(`appSecurity.php.origins.${v}`) },
          ]}
          refetchInterval={60_000}
          columns={[
            { key: 'branch', label: t('appSecurity.php.version'), width: 130, mono: true, accessor: (p: Php) => p.version ?? p.branch },
            // D58: « par défaut » and « FPM » in their own column, never badges after the version.
            { key: 'usage', label: t('appSecurity.php.usage'), width: 150, sortable: false,
              accessor: (p: Php) => [p.default ? t('appSecurity.php.default') : '', p.fpm ? t('appSecurity.php.fpm') : ''].filter(Boolean).join(' · '),
              render: (p: Php) => (p.default || p.fpm ? (
                <Inline gap={4} align="center" wrap={false}>
                  {p.default && <Badge tone="neutral" size="sm">{t('appSecurity.php.default')}</Badge>}
                  {p.fpm && <Badge tone="neutral" size="sm">{t('appSecurity.php.fpm')}</Badge>}
                </Inline>
              ) : null) },
            { key: 'state', label: t('appSecurity.php.state'), width: 300, render: (p: Php) => <PhpState php={p} /> },
            { key: 'package', label: t('appSecurity.php.package'), mono: true, grow: true, width: 200, sortable: false, render: (p: Php) => p.package ?? '—' },
            { key: 'origin', label: t('appSecurity.php.origin'), width: 130, render: (p: Php) => t(`appSecurity.php.origins.${p.origin}`) },
          ]}
          empty={<EmptyState framed={false} icon="code" title={t('appSecurity.php.empty')} />}
        />
      </Panel>}

      <Drawer
        open={Boolean(open)}
        onClose={() => setOpen(null)}
        eyebrow={t('appSecurity.drawer.eyebrow')}
        title={open?.path ?? ''}
        subtitle={open ? t('appSecurity.drawer.subtitle', { version: open.version, plugins: open.plugins.length, themes: open.themes.length }) : undefined}
        size="xl"
        footer={<Button variant="secondary" onClick={() => setOpen(null)}>{t('appSecurity.drawer.close')}</Button>}
      >
        {open && (
          <Stack gap={16}>
            <ListTable<Finding>
              label={t('appSecurity.drawer.findings')}
              queryKey={['server-running-list', server, 'wordpressFindings', open.path]}
              fetch={runningList<Finding>(server, 'wordpressFindings')}
              fixed={{ install: open.path }}
              rowKey="id"
              defaultSort={{ key: 'severity', dir: 'asc' }}
              search={t('appSecurity.drawer.search')}
              facets={[
                { key: 'severity', label: t('appSecurity.drawer.severity'), valueLabel: v => (v === 'none' ? '—' : t(`appSecurity.severity.${v}`)) },
                { key: 'kind', label: t('appSecurity.drawer.kind'), valueLabel: v => t(`appSecurity.kind.${v}`) },
              ]}
              refetchInterval={false}
              columns={[
                { key: 'name', label: t('appSecurity.drawer.component'), width: 160, mono: true },
                // D58: the component's kind in its own column, never a badge after its name.
                { key: 'kind', label: t('appSecurity.drawer.kind'), width: 90, sortable: false, render: (f: Finding) => <Badge tone="neutral" size="sm">{t(`appSecurity.kind.${f.kind}`)}</Badge> },
                { key: 'version', label: t('appSecurity.wordpress.version'), mono: true, width: 90, sortable: false, accessor: (f: Finding) => f.version ?? '—' },
                { key: 'severity', label: t('appSecurity.drawer.severity'), width: 130, render: (f: Finding) => (f.v.severity
                  ? <Badge tone={SEVERITY_TONES[f.v.severity]} size="sm">{`${t(`appSecurity.severity.${f.v.severity}`)}${f.v.score !== null ? ` · ${f.v.score}` : ''}`}</Badge>
                  : <Text variant="body-sm" tone="mute">—</Text>) },
                // The title, then its reference (a link when the source gives one), on one line.
                { key: 'title', label: t('appSecurity.drawer.vulnerability'), grow: true, width: 200, sortable: false, render: (f: Finding) => (
                  <Inline gap={8} align="center" wrap={false}>
                    <Text variant="body-sm" truncate>{f.v.title}</Text>
                    {f.v.id && (f.v.link ? <Link href={f.v.link} external>{f.v.id}</Link> : <Text variant="caption">{f.v.id}</Text>)}
                  </Inline>
                ) },
                { key: 'fixedIn', label: t('appSecurity.drawer.fixedIn'), width: 130, sortable: false, render: (f: Finding) => (f.v.unfixed
                  ? <Badge tone="neg" size="sm">{t('appSecurity.drawer.unfixed')}</Badge>
                  : <Text variant="code">{f.v.fixedIn ?? '—'}</Text>) },
              ]}
              empty={<EmptyState framed={false} icon="verified" title={open.checked ? t('appSecurity.drawer.none') : t('appSecurity.state.unchecked')} />}
            />
            <Text variant="body-sm" tone="mute">{t('appSecurity.drawer.source')}</Text>
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}
